import { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import paths from '@src/paths';

const PRIORITY_LABEL = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const SORT_FIELD_OPTIONS = [
  { value: 'created_at', label: 'Created date' },
  { value: 'due_date', label: 'Due date' },
];

const SORT_DIRECTION_OPTIONS = [
  { value: 'asc', label: 'Oldest first' },
  { value: 'desc', label: 'Newest first' },
];

const WORK_DECISION_LABELS = {
  no_reply: 'No reply',
  reply_only: 'Reply only',
  reply_and_task: 'Reply + task',
  task_only: 'Task only',
  needs_human_review: 'Needs review',
};

function formatCategory(value) {
  return value ? value.replace(/_/g, ' ') : '';
}

function DetailBlock({ label, children }) {
  if (!children) {
    return null;
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
        {children}
      </Typography>
    </Box>
  );
}

DetailBlock.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

DetailBlock.defaultProps = {
  children: null,
};

function TaskTable({
  tasks,
  loading,
  statusOptions,
  sortConfig,
  onEdit,
  onStatusChange,
  onToggleComplete,
  onDelete,
  onSortChange,
}) {
  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpanded = (taskId) => {
    setExpandedRows((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const expandedCount = useMemo(
    () => Object.values(expandedRows).filter(Boolean).length,
    [expandedRows],
  );

  return (
    <Card variant="outlined">
      <CardHeader
        title="Table view"
        subheader={`Built for triage and detail review. ${expandedCount ? `${expandedCount} row${expandedCount === 1 ? '' : 's'} expanded.` : ''}`}
        action={
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ minWidth: { md: 380 } }}>
            <TextField
              select
              size="small"
              label="Sort by"
              value={sortConfig.field}
              onChange={(event) => onSortChange(event.target.value, sortConfig.direction)}
            >
              {SORT_FIELD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Order"
              value={sortConfig.direction}
              onChange={(event) => onSortChange(sortConfig.field, event.target.value)}
            >
              {SORT_DIRECTION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        }
      />
      <CardContent>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No tasks found. Try adjusting your filters or create a new task.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell padding="checkbox">Complete</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Due date</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => {
                  const isExpanded = Boolean(expandedRows[task.id]);
                  const hasDetails = Boolean(
                    task.source_email_thread_id ||
                    task.notes ||
                    task.description ||
                    task.source_email_summary ||
                    task.source_email_router_reason ||
                    (task.subtasks || []).length,
                  );

                  return (
                    <Fragment key={task.id}>
                      <TableRow hover>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(task.id)}
                            disabled={!hasDetails}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={Boolean(task.is_completed)}
                            onChange={(event) => onToggleComplete(task, event.target.checked)}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 320, maxWidth: 420 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {task.title}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                            {task.source_email_thread_id ? (
                              <Chip size="small" label="From inbox" color="secondary" variant="outlined" />
                            ) : null}
                            {task.source_email_work_decision ? (
                              <Chip
                                size="small"
                                label={WORK_DECISION_LABELS[task.source_email_work_decision] || task.source_email_work_decision}
                                variant="outlined"
                              />
                            ) : null}
                            {task.source_email_router_category ? (
                              <Chip size="small" label={formatCategory(task.source_email_router_category)} variant="outlined" />
                            ) : null}
                            {(task.subtasks || []).length ? (
                              <Chip size="small" label={`${task.subtasks.length} subtasks`} variant="outlined" />
                            ) : null}
                          </Stack>
                          {task.source_email_summary ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
                              {task.source_email_summary}
                            </Typography>
                          ) : task.notes ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
                              {task.notes}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{task.client_name || '—'}</TableCell>
                        <TableCell sx={{ minWidth: 170 }}>
                          {task.assignee_name ? (
                            <Chip size="small" color="primary" variant="outlined" label={task.assignee_name} />
                          ) : (
                            <Chip size="small" label="Unassigned" />
                          )}
                        </TableCell>
                        <TableCell>
                          {task.due_date ? dayjs(task.due_date).format('MMM D, YYYY') : '—'}
                        </TableCell>
                        <TableCell>{PRIORITY_LABEL[task.priority] || task.priority_display || '—'}</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={task.status || 'backlog'}
                            onChange={(event) => onStatusChange(task, event.target.value)}
                          >
                            {statusOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit task">
                            <IconButton size="small" onClick={() => onEdit(task)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete task">
                            <IconButton size="small" onClick={() => onDelete(task)} sx={{ color: 'error.main' }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, py: 2.5, backgroundColor: (theme) => theme.palette.action.hover }}>
                              <Stack spacing={2.5}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                                  <Box sx={{ flex: 1 }}>
                                    <DetailBlock label="What needs to be done">
                                      {task.notes || task.description || 'No task notes available.'}
                                    </DetailBlock>
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <DetailBlock label="Assignee">
                                      {task.assignee_name || 'Unassigned'}
                                    </DetailBlock>
                                    <DetailBlock label="Source context">
                                      {task.source_email_subject || task.source_email_summary || 'Created manually.'}
                                    </DetailBlock>
                                    {task.source_email_thread_id ? (
                                      <Button
                                        component={RouterLink}
                                        to={`${paths.clientInbox}?thread=${task.source_email_thread_id}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ mt: 1.25 }}
                                      >
                                        Open source inbox thread
                                      </Button>
                                    ) : null}
                                  </Box>
                                </Stack>
                                {(task.source_email_summary || task.source_email_router_reason) ? (
                                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                                    <Box sx={{ flex: 1 }}>
                                      <DetailBlock label="AI summary">
                                        {task.source_email_summary}
                                      </DetailBlock>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <DetailBlock label="Router reason">
                                        {task.source_email_router_reason}
                                      </DetailBlock>
                                    </Box>
                                  </Stack>
                                ) : null}
                                {(task.subtasks || []).length ? (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                      Subtasks
                                    </Typography>
                                    <Stack spacing={1.25} sx={{ mt: 1 }}>
                                      {task.subtasks.map((subtask) => (
                                        <Paper key={subtask.id} variant="outlined" sx={{ p: 1.5 }}>
                                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {subtask.title}
                                              </Typography>
                                              {subtask.notes ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                  {subtask.notes}
                                                </Typography>
                                              ) : null}
                                            </Box>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                              {subtask.assignee_name ? <Chip size="small" label={subtask.assignee_name} /> : null}
                                              {subtask.due_date ? (
                                                <Chip size="small" label={dayjs(subtask.due_date).format('MMM D, YYYY')} variant="outlined" />
                                              ) : null}
                                              <Chip size="small" label={PRIORITY_LABEL[subtask.priority] || subtask.priority_display || '—'} variant="outlined" />
                                            </Stack>
                                          </Stack>
                                        </Paper>
                                      ))}
                                    </Stack>
                                  </Box>
                                ) : null}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

TaskTable.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      source_email_thread_id: PropTypes.number,
      source_email_subject: PropTypes.string,
      source_email_summary: PropTypes.string,
      source_email_work_decision: PropTypes.string,
      source_email_router_category: PropTypes.string,
      source_email_router_reason: PropTypes.string,
      subtasks: PropTypes.arrayOf(PropTypes.object),
    }),
  ),
  loading: PropTypes.bool,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  sortConfig: PropTypes.shape({
    field: PropTypes.oneOf(['created_at', 'due_date']).isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onToggleComplete: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
};

TaskTable.defaultProps = {
  tasks: [],
  loading: false,
};

export default TaskTable;
